import { NavLink, Outlet } from 'react-router';

import { routes } from '@/app/routes';

import styles from './AccountLayout.module.css';

/**
 * Everything that belongs to the reader, behind one menu.
 *
 * Seven destinations had accumulated: the profile itself, measurements,
 * progress, history, equipment, reminders and integrations. Four of them sat in an
 * avatar dropdown, and the other three were tiles inside the profile page — a
 * submenu wearing the clothes of content, which is the thing that was moved out of
 * the page in the first place. The owner named the fix:
 *
 * > Ik denk dus dat we de avatar moeten linken naar een profiel pagina, die aan de
 * > linkerkant een menu heeft waarin je de profiel, metingen, voortgang,
 * > geschiedenis, integraties, materiaal en herinneringen kunt beheren.
 *
 * So this is a real section of the app with its own navigation, and the avatar is
 * a link into it rather than a menu button. A dropdown is right for three or four
 * items and wrong for seven.
 *
 * It is a **pathless layout route**, which is why none of the URLs moved:
 * `/voortgang` is still `/voortgang`. Renaming seven paths to `/profiel/*` would
 * have broken every link anybody had, for no gain the reader can see.
 */

interface AccountLink {
    to: string;
    label: string;
    /** One line, only where the destination is not obvious from its name. */
    hint?: string;
}

/*
 * Grouped, because seven flat rows is a list you have to read to the end of.
 *
 * "Jij" is the record of what you did and where your body is; "Instellingen" is
 * what the app should do with that. The order inside each group is the order
 * somebody visits them in.
 */
const GROUPS: readonly { title: string; items: readonly AccountLink[] }[] = [
    {
        title: 'Jij',
        items: [
            { to: routes.settings, label: 'Profiel' },
            { to: routes.progress, label: 'Voortgang' },
            { to: routes.history, label: 'Geschiedenis' },
            { to: routes.measurements, label: 'Metingen' },
        ],
    },
    {
        title: 'Instellingen',
        items: [
            { to: routes.equipment, label: 'Materiaal' },
            { to: routes.settingsReminders, label: 'Herinneringen' },
            { to: routes.settingsIntegrations, label: 'Koppelingen' },
        ],
    },
];

export function AccountLayout() {
    return (
        <div className={styles.layout}>
            {/*
                A second navigation landmark, named. Two unnamed `<nav>` elements
                on one page leave a screen-reader user choosing between "navigation"
                and "navigation".
            */}
            <nav className={styles.menu} aria-label="Jouw gegevens en instellingen">
                {GROUPS.map((group) => (
                    <div key={group.title} className={styles.group}>
                        <p className={styles.groupTitle}>{group.title}</p>

                        <ul className={styles.list}>
                            {group.items.map((item) => (
                                <li key={item.to}>
                                    <NavLink
                                        to={item.to}
                                        end
                                        className={({ isActive }) =>
                                            `${styles.link} ${isActive ? styles.linkActive : ''}`
                                        }
                                    >
                                        {item.label}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </nav>

            <div className={styles.content}>
                <Outlet />
            </div>
        </div>
    );
}
